/**
 * Klantlogboek op de tab Contact (sessie 162). Twee blokken:
 * - "Afspraken en open toezeggingen": de `bovenaan`-regels uit het logboek
 *   (afspraken eerst, dan open toezeggingen op termijn), samen met de wekkers
 *   en oude commitments van de kaart (`kaart.beloftes`, het vroegere blok
 *   "Open belofte"). Een wekker van soort belofte op een zaak waarvoor het
 *   logboek al een open toezegging heeft, staat er niet dubbel: dan alleen de
 *   toezegging, met "wekker staat" erbij.
 * - "Logboek": nieuwste eerst, per 30 regels, met "Meer laden".
 * Beide blokken lezen dezelfde query (`kaart10Sleutels.logboek`).
 * Een regel uit een mail ("Wij schreven", en sinds sessie 141 A2 ook "Klant
 * vertelde" uit een mail van de klant) opent de mail zelf met `MailWeergave`,
 * dezelfde weergave als in de zaak (sessie 186, punt 7).
 * Vorm van de route: `app/schemas/klantlogboek.py`.
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { KaartBelofte } from "../../Klanten/types";
import { MailUitLogboek, zonderMarkering } from "../../Midden/MailWeergave";
import {
  kaart10Api,
  kaart10Sleutels,
  type KaartLogboek,
  type KaartLogregel,
  type LogboekCursor,
} from "./api";
import { dagDatum } from "./hulp";

const DATUM = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** "08-10-2026": de datum van een logboekregel, in Nederlandse tijd. */
export function korteDatum(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const delen = Object.fromEntries(DATUM.formatToParts(d).map((p) => [p.type, p.value]));
  return `${delen.day}-${delen.month}-${delen.year}`;
}

export function useLogboek(klantId: string) {
  return useInfiniteQuery({
    queryKey: kaart10Sleutels.logboek(klantId),
    queryFn: ({ pageParam }) => kaart10Api.logboek(klantId, pageParam),
    initialPageParam: null as LogboekCursor | null,
    getNextPageParam: (laatste: KaartLogboek) => {
      const slot = laatste.regels[laatste.regels.length - 1];
      return laatste.heeft_meer && slot ? { tijd: slot.tijd, id: slot.id } : undefined;
    },
  });
}

type LogboekQuery = ReturnType<typeof useLogboek>;

const BELOFTE_LABEL: Record<KaartBelofte["soort"], string> = {
  belofte: "Belofte",
  klacht: "Klacht",
  wekker: "Wekker",
  toezegging: "Toezegging",
};

/** Een regel in het blok "Afspraken en open toezeggingen". */
export interface Bovenregel {
  sleutel: string;
  soort: string;
  tekst: string;
  /** Afspraak: geen termijn. Toezegging of wekker: "voor <datum>". */
  termijn: string | null;
  overTijd: boolean;
  wekker: boolean;
  afspraak: boolean;
}

function opTermijn(a: Bovenregel, b: Bovenregel): number {
  if (a.termijn === b.termijn) return 0;
  if (a.termijn === null) return 1;
  if (b.termijn === null) return -1;
  return Date.parse(a.termijn) - Date.parse(b.termijn);
}

/**
 * Afspraken eerst (in de volgorde van de backend), dan de open toezeggingen
 * uit het logboek en de wekkers en commitments van de kaart samen, vroegste
 * termijn eerst. Een wekker van soort belofte op een zaak met een open
 * toezegging in het logboek gaat op in die toezegging.
 */
export function bovenaanRegels(bovenaan: KaartLogregel[], beloftes: KaartBelofte[]): Bovenregel[] {
  const afspraken = bovenaan.filter((r) => r.soort === "afspraak");
  const toezeggingen = bovenaan.filter((r) => r.soort !== "afspraak");
  const zakenMetToezegging = new Set(
    toezeggingen.map((r) => r.zaak_id).filter((z): z is string => Boolean(z)),
  );
  const zakenMetWekker = new Set<string>();
  const overigeBeloftes = beloftes.filter((b) => {
    if (b.soort === "belofte" && b.zaak_id && zakenMetToezegging.has(b.zaak_id)) {
      if (b.wekker_staat) zakenMetWekker.add(b.zaak_id);
      return false;
    }
    return true;
  });

  const vast: Bovenregel[] = afspraken.map((r) => ({
    sleutel: `log-${r.id}`,
    soort: r.soort_label,
    tekst: r.tekst,
    termijn: null,
    overTijd: false,
    wekker: false,
    afspraak: true,
  }));
  const open: Bovenregel[] = [
    ...toezeggingen.map((r) => ({
      sleutel: `log-${r.id}`,
      soort: r.soort_label,
      tekst: r.tekst,
      termijn: r.termijn_op,
      overTijd: r.over_tijd,
      wekker: r.zaak_id !== null && zakenMetWekker.has(r.zaak_id),
      afspraak: false,
    })),
    ...overigeBeloftes.map((b, i) => ({
      sleutel: `kaart-${b.soort}-${i}`,
      soort: BELOFTE_LABEL[b.soort] ?? b.soort,
      tekst: b.tekst,
      termijn: b.uiterlijk,
      overTijd: false,
      wekker: b.wekker_staat,
      afspraak: false,
    })),
  ].sort(opTermijn);
  return [...vast, ...open];
}

export function AfsprakenBlok({ q, beloftes }: { q: LogboekQuery; beloftes: KaartBelofte[] }) {
  const bovenaan = q.data?.pages[0]?.bovenaan ?? [];
  const regels = bovenaanRegels(bovenaan, beloftes);
  let leeg = "Geen afspraken of open toezeggingen.";
  if (q.isLoading) leeg = "Afspraken worden geladen.";
  else if (q.isError) leeg = "Afspraken uit het logboek konden niet geladen worden.";

  return (
    <div
      className={`kk10-blok${regels.length > 0 ? " kk10-belofte" : ""}`}
      data-testid="belofte"
    >
      <div className="bh">Afspraken en open toezeggingen</div>
      {regels.length === 0 ? (
        <p className="kk10-in kk10-leeg stil">{leeg}</p>
      ) : (
        <ul className="kk10-log">
          {regels.map((r) => (
            <li key={r.sleutel} data-afspraak={r.afspraak ? "ja" : undefined}>
              <div className="kop">
                <span className="s">{r.soort}</span>
                {r.termijn ? <span className="num">{`voor ${dagDatum(r.termijn)}`}</span> : null}
                {r.overTijd ? <span className="kk10-pil over">over tijd</span> : null}
                {r.wekker ? <span>wekker staat</span> : null}
              </div>
              <div className="t">{r.tekst}</div>
            </li>
          ))}
        </ul>
      )}
      {regels.length > 0 && q.isError ? (
        <p className="kk10-in kk10-leeg stil" role="status">
          Afspraken uit het logboek konden niet geladen worden.
        </p>
      ) : null}
    </div>
  );
}

function statusVan(r: KaartLogregel): string | null {
  if (r.soort === "toezegging") return r.status;
  if (r.soort === "afspraak" && r.status === "vervallen") return r.status;
  return null;
}

/**
 * Een regel die uit een mail komt ("Wij schreven", sessie 186 punt 7): de
 * tekst zonder markeringen, en de mail zelf achter "Mail tonen", in dezelfde
 * weergave als in de zaak (`MailWeergave`, eerdere berichten ingeklapt).
 */
function MailRegelInhoud({ r, emailId }: { r: KaartLogregel; emailId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="t">{zonderMarkering(r.tekst)}</div>
      <button
        type="button"
        className="kk10-mail-knop"
        data-testid="logboek-mail-tonen"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? "Mail verbergen" : "Mail tonen"}
      </button>
      {open ? (
        <div className="kk10-mail" data-testid="logboek-mail">
          <MailUitLogboek emailId={emailId} />
        </div>
      ) : null}
    </>
  );
}

function LogRegel({ r }: { r: KaartLogregel }) {
  const status = statusVan(r);
  const wie = [r.kanaal_label, r.door_label].filter(Boolean).join(", ");
  // Een feit uit een mail ("Klant vertelde", sessie 141 A2) draagt `<mail-id>#<n>`.
  const emailId = r.bron_soort === "email" && r.bron_id ? r.bron_id.split("#")[0] : null;
  return (
    <li data-geschiedenis={r.uit_geschiedenis ? "ja" : undefined}>
      <div className="kop">
        <span className="num">{korteDatum(r.tijd)}</span>
        <span className="s">{r.soort_label}</span>
        {wie ? <span>{wie}</span> : null}
        {status ? <span className={`kk10-pil ${status}`}>{status}</span> : null}
        {r.soort === "toezegging" && r.status === "open" && r.termijn_op ? (
          <span className="num">{`voor ${korteDatum(r.termijn_op)}`}</span>
        ) : null}
        {r.over_tijd ? <span className="kk10-pil over">over tijd</span> : null}
      </div>
      {emailId ? <MailRegelInhoud r={r} emailId={emailId} /> : <div className="t">{r.tekst}</div>}
    </li>
  );
}

export function LogboekBlok({ q }: { q: LogboekQuery }) {
  const gezien = new Set<string>();
  const regels: KaartLogregel[] = [];
  for (const pagina of q.data?.pages ?? []) {
    for (const r of pagina.regels) {
      if (gezien.has(r.id)) continue;
      gezien.add(r.id);
      regels.push(r);
    }
  }

  let inhoud;
  if (q.isLoading) inhoud = <p className="kk10-in kk10-leeg stil">Logboek wordt geladen.</p>;
  else if (q.isError && regels.length === 0)
    inhoud = (
      <div className="kk10-in">
        <p className="v6-fout" role="alert">
          Het logboek kon niet geladen worden.
        </p>
      </div>
    );
  else if (regels.length === 0)
    inhoud = <p className="kk10-in kk10-leeg stil">Nog niets in het logboek.</p>;
  else
    inhoud = (
      <ul className="kk10-log">
        {regels.map((r) => (
          <LogRegel key={r.id} r={r} />
        ))}
      </ul>
    );

  return (
    <div className="kk10-blok" data-testid="logboek">
      <div className="bh">Logboek</div>
      {inhoud}
      {q.isFetchNextPageError ? (
        <div className="kk10-in">
          <p className="v6-fout" role="alert">
            Meer laden lukte niet. Probeer het nog eens.
          </p>
        </div>
      ) : null}
      {q.hasNextPage && regels.length > 0 ? (
        <div className="kk10-in kk10-meer-rij">
          <button
            type="button"
            className="v6-knop kk10-meer"
            disabled={q.isFetchingNextPage}
            aria-busy={q.isFetchingNextPage || undefined}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? "Bezig met laden." : "Meer laden"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
